import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Tooltip, { TooltipProps } from 'ext-ui/components/Tooltip/Tooltip'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ExtendLocales } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectBrandColorCta,
  selectBrandColorHighlight,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

export interface OnOffSwitchProps {
  testId?: string
  onChange?: (bool: boolean) => Promise<boolean> | boolean | void
  defaultOnOffState?: boolean
  onText?: string
  offText?: string
  disabled?: boolean
  onBgColor?: string
  smallMode?: boolean
  tooltipProps?: TooltipProps
}

const OnOffSwitch: React.FC<OnOffSwitchProps> = (props): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const brandColorCta = useSelector(selectBrandColorCta)
  const brandColorHighlight = useSelector(selectBrandColorHighlight)
  const translate = useTranslate()

  const [isChecked, setIsChecked] = useState(!!props.defaultOnOffState)
  const {
    enterDelay = 750,
    tooltipContent,
    ...restTooltipProps
  } = props.tooltipProps || {}

  // redundant hook to update appearance when defaultOnOffState changes externally
  useEffect(() => {
    if (isChecked !== props.defaultOnOffState) {
      setIsChecked(!!props.defaultOnOffState)
    }
  }, [props.defaultOnOffState])

  const onSwitchChange = async (e: React.SyntheticEvent): Promise<void> => {
    if (props.disabled) return

    const isCheckedPrev = isChecked

    e.preventDefault()
    e.stopPropagation()
    setIsChecked(!isCheckedPrev)

    const changeSucceeded = await props.onChange?.(!isCheckedPrev)

    // if onChange is undefined, onChange returns undefined (void), or onChange returns true, do nothing
    if (changeSucceeded === false) {
      setIsChecked(isCheckedPrev)
    }
  }

  const onColor = props.onBgColor || brandColorHighlight || brandColorCta

  return (
    <Tooltip
      tooltipContent={tooltipContent}
      enterDelay={enterDelay}
      {...restTooltipProps}
    >
      <div
        data-testid={props.testId}
        css={css`
          appearance: none;
          ${!props.smallMode
            ? css`
                min-width: 175px;
              `
            : css`
                width: 34px;
              `}
          ${!props.smallMode
            ? css`
                min-height: 34px;
              `
            : css`
                height: 22px;
              `}
        margin: 0;

          position: relative;
          background-color: ${styleConstants.colors.gray_medium};
          ${!props.smallMode
            ? css`
                border-radius: 6px;
              `
            : css`
                border-radius: 11px;
              `}
          border: 1px solid ${styleConstants.colors.gray_medium};

          cursor: pointer;
          user-select: none;
          transition:
            background-color 0.3s,
            border-color 0.3s;

          ${props.disabled &&
          css`
            opacity: 0.5;
          `}

          &::before {
            content: attr(data-off);
            position: absolute;
            width: 50%;
            top: 2px;
            left: 2px;
            bottom: 2px;
            padding-top: 0.4em;
            background-color: white;
            transition: all 0.3s;
            ${!props.smallMode
              ? css`
                  border-radius: 6px;
                `
              : css`
                  border-radius: 11px;
                `}
            cursor: pointer;
            z-index: 1;
            text-align: center;
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            box-shadow: 0 3px 5px ${styleConstants.colors.gray_medium};
          }

          &::after {
            content: attr(data-on);
            display: block;
            position: absolute;
            line-height: 1;
            top: 0.5em;
            right: 2.5em;
            bottom: 0.5em;
            text-align: center;
            ${getFontCss(
              14,
              styleConstants.colors.gray_dark,
              styleConstants.fontFamilies.regular,
            )};
            ${locale === ExtendLocales.frCA &&
            css`
              right: 1.7em;
            `}
          }

          ${isChecked &&
          css`
            background-color: ${onColor};
            border-color: ${onColor};

            &::before {
              content: attr(data-on);
              left: 0;
              transform: translateX(${props.smallMode ? '90%' : '97%'});
              box-shadow: none;
            }

            &::after {
              content: attr(data-off);
              position: absolute;
              width: 50%;
              top: 2px;
              left: 2px;
              bottom: 2px;
              padding-top: 0.4em;
              transition: all 0.3s;
              ${!props.smallMode
                ? css`
                    border-radius: 6px;
                  `
                : css`
                    border-radius: 11px;
                  `}
              cursor: pointer;
              z-index: 1;
              text-align: center;
              ${getFontCss(
                14,
                styleConstants.colors.gray_light,
                styleConstants.fontFamilies.regular,
              )};
            }
          `}
        `}
        role={'switch'}
        data-on={props.smallMode ? ' ' : props.onText || translate('On')}
        data-off={props.smallMode ? ' ' : props.offText || translate('Off')}
        onClick={onSwitchChange}
        onKeyPress={onSwitchChange}
        aria-checked={isChecked}
        tabIndex={0}
      />
    </Tooltip>
  )
}

export default OnOffSwitch
